import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import { ClienteService } from '../../../../services/ClienteService';
import Http from '../../../../Http';

export const ClienteVentas = ({ match, id: propId, onSave }) => {
  const routeParams = useParams() || {};
  const clientId = propId || match?.params?.id || routeParams?.id;

  const [giros, setGiros] = useState([]);
  const [ejecutivos, setEjecutivos] = useState([]);

  // Carga catálogos estáticos desde la caché unificada
  useEffect(() => {
    let mounted = true;
    const fetchOptions = async () => {
      try {
        const opts = await ClienteService.obtenerCatalogoOptions();
        if (mounted && opts?.giros) {
          setGiros(
            opts.giros.map((g) => ({
              value: String(g.id),
              label: g.giro_nombre,
            }))
          );
        }
      } catch (err) {
        console.error('Error al cargar catálogo de giros', err);
      }
    };
    fetchOptions();
    return () => {
      mounted = false;
    };
  }, []);

  // Cargar datos de venta del cliente en un solo round-trip
  const loadClientData = useCallback(async () => {
    if (!clientId) return null;
    try {
      const data = await ClienteService.obtenerDetalleCompleto(clientId);
      if (!data) return null;

      // Si tiene ejecutivo cargado, asegurar opción disponible
      if (data.cliente_id_ejecutivo && data.cliente_ejecutivo_nombre) {
        setEjecutivos([
          {
            value: String(data.cliente_id_ejecutivo),
            label: data.cliente_ejecutivo_nombre,
            ejecutivo: data.ejecutivo || '',
          },
        ]);
      }

      return {
        cliente_id_giro_negocio: data.cliente_id_giro_negocio ? String(data.cliente_id_giro_negocio) : '',
        cliente_importacion: Boolean(data.cliente_importacion),
        cliente_id_ejecutivo: data.cliente_id_ejecutivo ? String(data.cliente_id_ejecutivo) : '',
        ejecutivo: data.ejecutivo || '',
        cliente_ejecutivo_nombre: data.cliente_id_ejecutivo ? String(data.cliente_id_ejecutivo) : '',
        cliente_fecha_neg: data.cliente_fecha_neg ? String(data.cliente_fecha_neg).substring(0, 10) : '',
        cliente_observaciones: data.cliente_observaciones || '',
        cliente_referencia: data.cliente_referencia || '',
        cliente_convenio: data.cliente_convenio || '',
      };
    } catch (e) {
      console.error('Error al cargar datos comerciales del cliente', e);
      return null;
    }
  }, [clientId]);

  // Búsqueda remota de ejecutivos
  const handleSearchEjecutivos = useCallback(async (query) => {
    const list = await ClienteService.buscarEjecutivos(query);
    const options = list.map((e) => ({
      value: String(e.id),
      label: e.ejecutivo_nombre,
      ejecutivo: e.ejecutivo || '',
    }));
    setEjecutivos(options);
    return options;
  }, []);

  const handleEjecutivoSelect = useCallback(
    async (val, { setFieldValue }) => {
      if (val) {
        setFieldValue('cliente_id_ejecutivo', String(val));
        const found = ejecutivos.find((e) => String(e.value) === String(val));
        if (found) {
          setFieldValue('ejecutivo', found.ejecutivo || '');
        } else {
          try {
            const res = await Http.get(`ejecutivo/one/${val}`);
            if (res && !res.error && res.data) {
              setFieldValue('ejecutivo', res.data.ejecutivo || '');
            }
          } catch (err) {
            console.error('Error al consultar ejecutivo', err);
          }
        }
      } else {
        setFieldValue('cliente_id_ejecutivo', '');
        setFieldValue('ejecutivo', '');
      }
    },
    [ejecutivos]
  );

  const fields = useMemo(
    () => [
      {
        name: 'section_comercial',
        label: 'Información Comercial y Negociación',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_id_giro_negocio',
        label: 'Giro del Negocio',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El giro del negocio es requerido')],
        options: giros,
        placeholder: 'Seleccione un giro',
      },
      {
        name: 'cliente_importacion',
        label: 'Maneja mercancía de importación',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'ejecutivo',
        label: 'Clave Ejecutivo',
        type: 'text',
        col: 3,
        disabled: true,
        placeholder: 'Clave autoasignada',
      },
      {
        name: 'cliente_ejecutivo_nombre',
        label: 'Ejecutivo Asignado',
        type: 'autocomplete',
        col: 5,
        required: true,
        validators: [Validators.required('El ejecutivo es requerido')],
        options: ejecutivos,
        onSearch: handleSearchEjecutivos,
        onChange: handleEjecutivoSelect,
        placeholder: 'Buscar ejecutivo...',
      },
      {
        name: 'cliente_fecha_neg',
        label: 'Fecha de Negociación',
        type: 'date',
        col: 4,
        required: true,
        validators: [Validators.required('La fecha de negociación es requerida')],
      },
      {
        name: 'cliente_referencia',
        label: 'Referencia',
        type: 'text',
        col: 6,
        placeholder: 'Referencia de venta',
      },
      {
        name: 'cliente_convenio',
        label: 'Convenio Global (Clave Cliente Ref.)',
        type: 'text',
        col: 6,
        placeholder: 'Especificar clave si aplica',
      },
      {
        name: 'cliente_observaciones',
        label: 'Observaciones Comerciales',
        type: 'textarea',
        col: 12,
        required: true,
        validators: [Validators.required('Las observaciones son requeridas')],
        placeholder: 'Detalles y acuerdos comerciales pactados con el cliente...',
      },
    ],
    [giros, ejecutivos, handleSearchEjecutivos, handleEjecutivoSelect]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const payload = {
        id: clientId,
        ...values,
      };
      return await ClienteService.guardar(payload);
    },
    [clientId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response);
      }
    },
    [onSave]
  );

  return (
    <div className="space-y-6">
      <CatalogForm
        title="Datos de Ventas y Comercial"
        subtitle="Asignación de ejecutivo, giro y acuerdos comerciales"
        fields={fields}
        id={clientId}
        loadData={loadClientData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        submitButtonText="Guardar Datos de Ventas"
      />
    </div>
  );
};

export default withRouter(ClienteVentas);
